import type { TrainingExerciseDto, TrainingPlanDto, TrainingVideoDto } from '@ukinfo/shared';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect, useSyncExternalStore } from 'react';
import { ApiError, api, isNetworkError, isServerUnreachable, subscribeReachability } from './api';
import { useAuth } from './auth';
import { OfflineDataWipedError, offlineGeneration, rememberActiveUser, resetRemembered } from './offline-account';
import { entryKey, offlineStore, offlineSupported, videoKey, type OfflineEntry } from './offline-store';
import type { Season, Session, Team } from './sports';
import { itemExerciseId, TRAINING_PLANS_RESOURCE } from './training';

/**
 * Einheiten fuer den Platz ohne Netz: speichern, auffrischen, aufraeumen.
 *
 * Was gespeichert wird, bestimmt der Trainer (Knopf "Für offline speichern")
 * oder der automatische Abgleich beim Start der App: die Einheiten der
 * Trainings der naechsten 48 Stunden, ohne Videos. Gespeichert wird je
 * Konto getrennt; beim Abmelden verschwindet alles (`wipeOfflineData`).
 */

/* ------------------------------------------------------------------ Netz */

function subscribe(listener: () => void): () => void {
  window.addEventListener('online', listener);
  window.addEventListener('offline', listener);
  const unsubscribe = subscribeReachability(listener);
  return () => {
    window.removeEventListener('online', listener);
    window.removeEventListener('offline', listener);
    unsubscribe();
  };
}

/**
 * Hat das Gerät gerade Netz? Der Browser meldet es (online/offline) - und auch eine Anfrage, die am Netz
 * scheitert, zählt: "online" meldet er schon bei einem WLAN ohne Internet.
 */
export function useOnline(): boolean {
  return useSyncExternalStore(subscribe, () => navigator.onLine && !isServerUnreachable(), () => true);
}

/* ------------------------------------------------------- Einstellungen */

const AUTO_KEY = 'ukinfo.offline.auto';

/** Automatisches Vorhalten der nächsten Trainings - pro Gerät, Standard an. */
export function autoSyncEnabled(): boolean {
  try {
    return window.localStorage.getItem(AUTO_KEY) !== '0';
  } catch {
    return true;
  }
}

export function setAutoSyncEnabled(value: boolean): void {
  try {
    window.localStorage.setItem(AUTO_KEY, value ? '1' : '0');
  } catch {
    // Ohne localStorage bleibt es beim Standard.
  }
}

export { rememberActiveUser, storedActiveUser, wipeOfflineData } from './offline-account';

/* ------------------------------------------------------------ Inhalte */

export interface OfflineMetaInput {
  kind: 'session' | 'template';
  sessionId?: string | null;
  title: string;
  startAt?: string | null;
  endAt?: string | null;
  location?: string | null;
  teamName?: string | null;
}

/** Kennungen aller Übungen einer Einheit (ohne freie Einträge), ohne Wiederholung. */
export function planExerciseIds(plan: TrainingPlanDto): string[] {
  return [...new Set(plan.items.map(itemExerciseId).filter((id): id is string => id !== null))];
}

/**
 * Lädt die Übungen einer Einheit in voller Form. Eine Übung, die es nicht mehr gibt oder die
 * nicht mehr lesbar ist (404, 403), fehlt einfach - die Einheit zeigt dann nur ihre Kurzform.
 * Jeder andere Fehler, vor allem fehlendes Netz, bricht ab.
 */
export async function fetchExercises(
  plan: TrainingPlanDto,
  load: (id: string) => Promise<TrainingExerciseDto> = (id) => api.get<TrainingExerciseDto>(`/api/training/exercises/${id}`),
): Promise<Record<string, TrainingExerciseDto>> {
  const result: Record<string, TrainingExerciseDto> = {};
  const ids = planExerciseIds(plan);
  for (let index = 0; index < ids.length; index += 4) {
    await Promise.all(ids.slice(index, index + 4).map(async (id) => {
      try {
        result[id] = await load(id);
      } catch (error) {
        if (error instanceof ApiError && (error.status === 404 || error.status === 403)) return;
        throw error;
      }
    }));
  }
  return result;
}

/** Videos der Übungen, jedes einmal, in der Reihenfolge der Übungen. */
export function uniqueVideos(exercises: Record<string, TrainingExerciseDto>): TrainingVideoDto[] {
  const seen = new Set<string>();
  const videos: TrainingVideoDto[] = [];
  for (const exercise of Object.values(exercises)) {
    for (const video of exercise.videos ?? []) {
      if (seen.has(video.id)) continue;
      seen.add(video.id);
      videos.push(video);
    }
  }
  return videos;
}

export const videoBytes = (exercises: Record<string, TrainingExerciseDto>): number =>
  uniqueVideos(exercises).reduce((sum, video) => sum + video.sizeBytes, 0);

export type VideoMode = 'with' | 'without' | 'keep';

async function downloadVideo(video: TrainingVideoDto): Promise<Blob> {
  const response = await fetch(video.url, { credentials: 'same-origin' });
  if (!response.ok) throw new Error(`Ein Video ließ sich nicht laden (${response.status})`);
  return response.blob();
}

function storageError(error: unknown): Error {
  if (error instanceof DOMException && error.name === 'QuotaExceededError') {
    return new Error('Auf diesem Gerät ist nicht genug Speicher frei. Versuch es ohne Videos oder entferne andere gespeicherte Einheiten.');
  }
  return error instanceof Error ? error : new Error('Speichern auf dem Gerät fehlgeschlagen');
}

export interface SaveOfflineInput {
  userId: string;
  plan: TrainingPlanDto;
  exercises: Record<string, TrainingExerciseDto>;
  meta: OfflineMetaInput;
  /** `with`: Videos laden, `without`: keine, `keep`: vorhandene behalten (automatischer Abgleich). */
  videos: VideoMode;
  auto?: boolean;
  onProgress?: (done: number, total: number) => void;
  fetchVideo?: (video: TrainingVideoDto) => Promise<Blob>;
  /** Stand von `offlineGeneration()` zu Beginn des aufrufenden Vorgangs (Abgleich); sonst der beim Aufruf. */
  generation?: number;
}

/**
 * Speichert eine Einheit (neu oder aktualisiert) samt Übungen und auf Wunsch Videos. Wurde inzwischen
 * abgemeldet, schreibt sie nichts mehr und endet mit `OfflineDataWipedError`.
 */
export async function saveOffline(input: SaveOfflineInput): Promise<OfflineEntry> {
  const { userId, plan, exercises, meta } = input;
  const store = offlineStore();
  const generation = input.generation ?? offlineGeneration();
  // Unmittelbar vor jedem Schreiben pruefen - zwischen Pruefung und Schreibauftrag liegt kein await.
  const stillLoggedIn = (): void => {
    if (offlineGeneration() !== generation) throw new OfflineDataWipedError();
  };
  try {
    const existing = await store.getEntry(userId, plan.id);
    const videos = uniqueVideos(exercises);

    let videoIds: string[] = [];
    if (input.videos === 'with') {
      const fetchVideo = input.fetchVideo ?? downloadVideo;
      let done = 0;
      for (const video of videos) {
        if (!(await store.getVideo(userId, video.id))) {
          const blob = await fetchVideo(video);
          stillLoggedIn();
          await store.putVideo({ key: videoKey(userId, video.id), userId, videoId: video.id, mimeType: video.mimeType, size: blob.size, blob });
        }
        videoIds.push(video.id);
        input.onProgress?.(++done, videos.length);
      }
    } else if (input.videos === 'keep') {
      const current = new Set(videos.map((video) => video.id));
      videoIds = (existing?.videoIds ?? []).filter((id) => current.has(id));
    }

    const entry: OfflineEntry = {
      key: entryKey(userId, plan.id),
      userId,
      planId: plan.id,
      kind: meta.kind,
      sessionId: meta.sessionId ?? plan.sessionId ?? null,
      title: meta.title,
      startAt: meta.startAt ?? null,
      endAt: meta.endAt ?? null,
      location: meta.location ?? null,
      teamName: meta.teamName ?? plan.teamName ?? null,
      savedAt: new Date().toISOString(),
      // Was der Trainer selbst gespeichert hat, behandelt der Abgleich nie als "seins".
      auto: input.auto ? (existing?.auto ?? true) : false,
      plan,
      exercises,
      videoIds,
      bytes: JSON.stringify({ plan, exercises }).length,
    };

    // Ein Training hat nur eine Einheit: wurde sie ersetzt (neue ID), die alte Fassung entfernen.
    if (entry.sessionId) {
      for (const other of await store.listEntries(userId)) {
        if (other.sessionId === entry.sessionId && other.planId !== entry.planId) await store.deleteEntry(userId, other.planId);
      }
    }
    stillLoggedIn();
    await store.putEntry(entry);
    await dropOrphanVideos(userId);
    return entry;
  } catch (error) {
    throw storageError(error);
  }
}

/** Videos löschen, die keine gespeicherte Einheit mehr braucht. */
async function dropOrphanVideos(userId: string): Promise<void> {
  const store = offlineStore();
  const used = new Set((await store.listEntries(userId)).flatMap((entry) => entry.videoIds));
  for (const video of await store.listVideos(userId)) {
    if (!used.has(video.videoId)) await store.deleteVideo(userId, video.videoId);
  }
}

export async function removeOffline(userId: string, planId: string): Promise<void> {
  await offlineStore().deleteEntry(userId, planId);
  await dropOrphanVideos(userId);
}

export async function removeAllOffline(userId: string): Promise<void> {
  await offlineStore().clearUser(userId);
}

export interface OfflineUsage {
  entries: number;
  textBytes: number;
  videoBytes: number;
}

export async function offlineUsage(userId: string): Promise<OfflineUsage> {
  const store = offlineStore();
  const entries = await store.listEntries(userId);
  const videos = await store.listVideos(userId);
  return {
    entries: entries.length,
    textBytes: entries.reduce((sum, entry) => sum + entry.bytes, 0),
    videoBytes: videos.reduce((sum, video) => sum + video.size, 0),
  };
}

/** Gespeicherte Einheiten: Trainings nach Beginn (die kommenden zuerst), dann Vorlagen nach Name. */
export function sortEntries(entries: OfflineEntry[], now = Date.now()): OfflineEntry[] {
  const rank = (entry: OfflineEntry): number => {
    if (entry.kind === 'template') return 2;
    return entry.endAt && Date.parse(entry.endAt) < now ? 1 : 0;
  };
  return [...entries].sort((a, b) => {
    const byRank = rank(a) - rank(b);
    if (byRank !== 0) return byRank;
    if (a.kind === 'template') return a.title.localeCompare(b.title, 'de');
    const [x, y] = [Date.parse(a.startAt ?? '') || 0, Date.parse(b.startAt ?? '') || 0];
    // Kommende aufsteigend, vergangene absteigend.
    return rank(a) === 0 ? x - y : y - x;
  });
}

/* ----------------------------------------------------- automatischer Abgleich */

/** Zeitraum, für den Einheiten automatisch vorgehalten werden. */
export const AUTO_SYNC_HOURS = 48;
/** Nach dem Training bleibt die automatisch gespeicherte Einheit noch so lange. */
const KEEP_AFTER_MS = 24 * 3_600_000;

export interface SyncResult {
  saved: number;
  removed: number;
}

/**
 * Frischt die Einheiten der Trainings der nächsten 48 Stunden auf - für Mannschaften, die das Konto
 * führt (Anwesenheit erfassen). Videos bleiben, wie sie sind. Entfällt eine Einheit oder liegt das
 * Training lange zurück, verschwindet der automatisch angelegte Eintrag.
 */
export async function syncUpcoming(userId: string, now = new Date(), hours = AUTO_SYNC_HOURS): Promise<SyncResult> {
  const store = offlineStore();
  const result: SyncResult = { saved: 0, removed: 0 };
  // Der Abgleich dauert: wird unterdessen abgemeldet, speichert er nichts mehr (siehe saveOffline).
  const generation = offlineGeneration();

  for (const entry of await store.listEntries(userId)) {
    if (entry.auto && entry.endAt && Date.parse(entry.endAt) < now.getTime() - KEEP_AFTER_MS) {
      await removeOffline(userId, entry.planId);
      result.removed += 1;
    }
  }

  const until = now.getTime() + hours * 3_600_000;
  const seasons = await api.get<Season[]>('/api/sports/seasons');
  // Die laufende Saison - die Liste kann auch kommende enthalten.
  const today = now.toISOString().slice(0, 10);
  const seasonId = (seasons.find((season) => season.start_date <= today && today <= season.end_date) ?? seasons[0])?.id;
  if (!seasonId) return result;
  const teams = await api.get<Team[]>('/api/sports/teams', { seasonId });

  const due = new Map<string, { session: Session; teamName: string }>();
  for (const team of teams) {
    // Nur Mannschaften, die das Konto führt. (Nicht nach nextSession vorfiltern: ein Training, das gerade läuft, ist nicht mehr der nächste Termin.)
    if (!team.canManage) continue;
    let sessions: Session[];
    try {
      sessions = await api.get<Session[]>(`/api/sports/teams/${team.id}/sessions`, { seasonId });
    } catch (error) {
      // Eine Mannschaft, die sich nicht lesen lässt, hält die übrigen nicht auf.
      if (isNetworkError(error)) throw error;
      continue;
    }
    for (const session of sessions) {
      if (session.kind === 'WORK' || session.status === 'CANCELLED' || session.guest_of) continue;
      const end = Date.parse(session.end_at || session.start_at);
      if (end < now.getTime() || Date.parse(session.start_at) > until) continue;
      if (!due.has(session.id)) due.set(session.id, { session, teamName: team.name });
    }
  }

  for (const { session, teamName } of due.values()) {
    try {
      const plan = await api.get<TrainingPlanDto | null>(`/api/training/sessions/${session.id}/plan`);
      if (!plan) {
        for (const entry of await store.listEntries(userId)) {
          if (entry.sessionId === session.id) {
            await removeOffline(userId, entry.planId);
            result.removed += 1;
          }
        }
        continue;
      }
      const exercises = await fetchExercises(plan);
      await saveOffline({
        userId,
        plan,
        exercises,
        videos: 'keep',
        auto: true,
        generation,
        meta: { kind: 'session', sessionId: session.id, title: session.title, startAt: session.start_at, endAt: session.end_at, location: session.location, teamName },
      });
      result.saved += 1;
    } catch (error) {
      // Ohne Netz ist der Rest auch nicht zu holen, nach dem Abmelden ist nichts mehr zu speichern;
      // ein einzelner Fehler (Recht, Speicher) lässt die anderen Trainings gelten.
      if (isNetworkError(error) || error instanceof OfflineDataWipedError) throw error;
    }
  }
  return result;
}

/** Ein Lauf je App-Start genügt; zwischen zwei Läufen mindestens so lange warten. */
const MIN_INTERVAL_MS = 15 * 60_000;
let lastRun: { userId: string; at: number } | null = null;
let running = false;

/** Nur für Tests. */
export function resetAutoSync(): void {
  resetRemembered();
  lastRun = null;
  running = false;
}

/** Läuft der Abgleich, sobald die App geöffnet ist und das Gerät Netz hat. */
export async function runAutoSync(userId: string, queryClient?: QueryClient, now = Date.now()): Promise<SyncResult | null> {
  if (running || !offlineSupported() || !autoSyncEnabled() || !navigator.onLine) return null;
  if (lastRun && lastRun.userId === userId && now - lastRun.at < MIN_INTERVAL_MS) return null;
  running = true;
  try {
    const result = await syncUpcoming(userId, new Date(now));
    lastRun = { userId, at: now };
    await queryClient?.invalidateQueries({ queryKey: ['offline-entries'] });
    return result;
  } catch {
    // Im Hintergrund: beim nächsten Start noch einmal.
    return null;
  } finally {
    running = false;
  }
}

/* ----------------------------------------------------------------- Hooks */

/** Gilt für Konten mit Leserecht auf die Trainingsplanung - nicht in der Testansicht. */
export function useOfflineAvailable(): { userId: string | null; available: boolean } {
  const { user, can } = useAuth();
  const userId = user && !user.isImpersonating && can(TRAINING_PLANS_RESOURCE, 'read') ? user.userId : null;
  return { userId, available: userId !== null && offlineSupported() };
}

export function useOfflineEntries(userId: string | null) {
  return useQuery({
    queryKey: ['offline-entries', userId],
    queryFn: async () => (userId ? offlineStore().listEntries(userId) : []),
    enabled: userId !== null && offlineSupported(),
    staleTime: 0,
  });
}

/**
 * In der App: Konto merken und den Abgleich anstoßen. Mit kurzer Verzögerung, damit der Start der
 * Seite Vorrang hat.
 */
export function useOfflineAutoSync(): void {
  const { user } = useAuth();
  const { userId } = useOfflineAvailable();
  const queryClient = useQueryClient();
  const online = useOnline();
  const rememberId = user && !user.isImpersonating ? user.userId : null;

  useEffect(() => {
    if (rememberId) void rememberActiveUser(rememberId).catch(() => undefined);
  }, [rememberId]);

  useEffect(() => {
    if (!userId || !online) return;
    // Kein Aufräumen: ein Seitenwechsel soll den Abgleich nicht abbrechen; runAutoSync lässt nie zwei zugleich laufen.
    window.setTimeout(() => void runAutoSync(userId, queryClient), 3_000);
  }, [userId, online, queryClient]);
}
